import { Constants } from 'app/constants/constants';
import {
  AnalyticItemFilterSets,
  dateFilterChoices,
  Filter,
  FilterConfig,
  FilterSet,
  WidgetQueryOptions,
} from 'app/modules/collection/services/filter.service';
import { SelectedSection } from 'app/modules/widget/components/section-selector/section-selector.component';
import { Comparison } from 'app/providers/bridge-api/dashboard.service';

export class FilterUtils {
  /**
   * Compute the display value for a given filter value, based on the filter config.
   * If the value starts with 'numericrange/', it is split into parts and recombined
   * using the appropriate operator (OR or AND). If the value does not start with 'numericrange/',
   * it is returned as-is unless a filter config is provided, in which case the computeValue
   * function from the filter config is used to compute the display value.
   * @param analyticItem the name of the analytic item (e.g. 'phrases')
   * @param value the value of the analytic item filter (e.g. 'knowledge')
   * @param config the filter config for the given analytic item
   * @returns the computed value of the filter
   **/
  public static computeValue(
    analyticItem: string,
    value: string,
    config: FilterConfig,
    withQuotationMarks: boolean = true
  ): string {
    // Compute numeric ranges
    if (this.isNumericRange(value)) {
      return this.computeNumericRangeFilter(analyticItem, value);
    }

    // Compute date ranges
    if (this.isDateRange(value)) {
      return this.computeValueForDateFilter(value);
    }

    // Default compute
    if (!config || !config.computeValue) {
      return this.wrapWithQuotationMarks(this.escapeQuote(value), withQuotationMarks);
    }

    // Filt config specific compute
    return config.computeValue(value);
  }

  /**
   * Compute the display value for a given numeric range filter value.
   * The value is split into parts and recombined using the appropriate operator (OR or AND).
   * Each part is computed using the computeNumericRangePartValue function.
   * @param analyticItem the name of the analytic item (e.g. 'phrases')
   * @param value the value of the numeric range filter (e.g. 'numericrange/<=10 OR >=20')
   * @returns the computed value of the filter
   */
  public static computeNumericRangeFilter(analyticItem: string, value: string) {
    if (!this.isNumericRange(value)) {
      return value;
    }
    const computeNumericRangePartValue = function (part) {
      let computedValue;

      if (part.includes('<=')) {
        computedValue = `[* TO ${part.split('<=')[1].trim()}]`;
      } else if (part.includes('>=')) {
        computedValue = `[${part.split('>=')[1].trim()} TO *]`;
      } else if (part.includes('<')) {
        computedValue = `[* TO ${part.split('<')[1].trim()}}`;
      } else if (part.includes('>')) {
        computedValue = `{${part.split('>')[1].trim()} TO *]`;
      } else if (part.includes('!=')) {
        computedValue = `${part.split('!=')[1].trim()}`;
      } else if (part.includes('=')) {
        computedValue = `${part.split('=')[1].trim()}`;
      }

      return computedValue;
    };

    // Compute numeric ranges
    let parts = [value.replace('numericrange/', '')];
    let expr;
    if (value.includes('OR')) {
      parts = parts[0].split('OR');
      expr = 'OR';
    } else if (value.includes('AND')) {
      parts = parts[0].split('AND');
      expr = 'AND';
    }

    let computedValue = computeNumericRangePartValue(parts[0]);

    if (parts[1]) {
      computedValue = ` ${computedValue} ${expr} ${analyticItem}: ${computeNumericRangePartValue(
        parts[1]
      )}`;
    }
    return computedValue;
  }

  public static computeValueForDateFilter(key: string): string {
    let query = <string>dateFilterChoices.find((c) => c.key === key)?.value;

    if (this.isDateRange(key)) {
      const parts = key.split('/');
      query = `[${parts[1]} TO ${parts[2]}]`;
    }

    return query;
  }

  public static computeSelectedSectionBuckets(
    filterName: string,
    section: SelectedSection,
    config: FilterConfig,
    withQuotationMarks?: boolean
  ): string[] {
    return filterName && section?.buckets
      ? section.buckets.map((bucket: string) =>
          this.computeValue(filterName, bucket, config, withQuotationMarks)
        )
      : [];
  }

  /**
   * Checks if the given value starts with 'numericrange/'.
   * This is used to determine if a filter value is a numeric range filter.
   * @param value the value to check
   * @returns true if the value starts with 'numericrange/', false otherwise
   */
  public static isNumericRange(value: string): boolean {
    return value?.startsWith('numericrange/');
  }

  /**
   * Checks if the given value starts with 'daterange/'.
   * This is used to determine if a filter value is a date range filter.
   * @param value the value to check
   * @returns true if the value starts with 'daterange/', false otherwise
   */
  public static isDateRange(value: string): boolean {
    return value?.startsWith('daterange/');
  }

  /**
   * Checks whether section selector logic is available for the provided analytic item.
   * @param analyticItem the analytic item identifier to evaluate
   * @returns true when the analytic item supports section selection, false otherwise
   */
  public static isSectionSelectorAvailable(analyticItem: string, subType?: string): boolean {
    if (!analyticItem) {
      return false;
    }

    if (analyticItem === 'topics' && subType === 'auto') {
      return false;
    }

    return (
      Constants.SECTION_ALLOWED_ANALYTIC_ITEM_TYPES.includes(analyticItem) ||
      analyticItem.includes('sectionProperties')
    );
  }

  /**
   * Escapes a given string by replacing all occurrences of double quotes with escaped double quotes.
   * @param value the string to escape
   * @returns the escaped string
   */
  private static escapeQuote(value: string): string {
    return value.toString().replace(new RegExp('"', 'g'), '\\"');
  }

  /**
   * Escapes Lucene regex metacharacters by wrapping each in a character class [char].
   * Lucene's regex engine (used by Elasticsearch) does not support \-escaping,
   * so character classes are the standard way to match literal special characters.
   * @param value the string to escape
   * @returns the escaped string
   */
  public static escapeForLuceneRegex(value: string): string {
    return value.replace(/([.?+*|{}()\[\]\\#@&~<>"])/g, '[$1]');
  }

  /**
   * Escapes a value for use inside a Spotlight query regex (/.../).
   * Handles both Lucene regex metacharacters and the Spotlight parser's
   * regex delimiter (/).
   * @param value the string to escape
   * @returns the escaped string
   */
  public static escapeForSpotlightRegex(value: string): string {
    // First escape Lucene regex metacharacters
    let escaped = this.escapeForLuceneRegex(value);
    // Then escape / for the Spotlight query parser's regex delimiter.
    // The parser strips the \ before / when building the regex buffer.
    escaped = escaped.replace(/\//g, '\\/');
    return escaped;
  }

  /**
   * Escapes a value for use inside a Spotlight query regex (/.../) that matches strings starting with the value.
   * @param value the string to escape
   * @returns the escaped string
   */
  public static escapeTaxonomyStartingWith(value: string): string {
    return `${this.escapeForSpotlightRegex(value)}([|].*)?`;
  }

  /**
   * Converts an array of selected sections into an array of strings representing the filters for features.
   * Each string is in the format of "fieldName:bucket1,bucket2,..."
   * @param selectedSection An array of selected sections
   * @returns An array of strings representing the filters for features
   */
  public static convertSelectedSectionToFeatureSectionFilter(
    selectedSection: SelectedSection[]
  ): Array<string> {
    return (selectedSection ?? []).map((s) => {
      const prefix = this.sectionFilterPrefix(s);

      // Compute the filter name
      let filterName: string = s.field;
      if (s.type === 'property' || filterName.startsWith(Constants.SECTION_PROPERTIES_PREFIX)) {
        filterName = filterName.replace(Constants.SECTION_PROPERTIES_PREFIX, '');
      }

      // Compute the buckets as for the numeric ranges values should be transformed
      // (withQuotationMarks should be false here as it will be handled by wrapWithQuotationMarks through hasAnyRangeBucket)
      const computedBuckets = this.computeSelectedSectionBuckets(filterName, s, null, false);
      const hasAnyRangeBucket = s.buckets.some(
        (b: string) => this.isDateRange(b) || this.isNumericRange(b)
      );

      // Return the featureSectionFilter
      return `${prefix}${filterName}:${this.wrapWithQuotationMarks(computedBuckets.join(','), !hasAnyRangeBucket)}`;
    });
  }

  /**
   * Returns a prefix for the given section.
   * If the section is negated, it returns 'NOT '.
   * Otherwise, it returns an empty string.
   * @param section The section to get the prefix for
   * @returns The prefix for the given section
   */
  public static sectionFilterPrefix(section: SelectedSection) {
    return !!section?.negated ? 'NOT ' : '';
  }

  /**
   * Returns the join type for the given section's buckets.
   * If the section is negated, it returns 'AND'. Otherwise, it returns 'OR'.
   * @param section The section to get the join type for
   * @returns The join type for the given section's buckets
   */
  public static sectionFilterBucketJoinType(section: SelectedSection) {
    return !!section?.negated ? 'AND' : 'OR';
  }

  /**
   * Wraps the given value with parentheses if shouldWrap is true.
   * @param value The value to wrap
   * @param shouldWrap Whether to wrap the value with parentheses
   * @returns The wrapped value if shouldWrap is true, the original value otherwise
   */
  public static wrapWithParentheses(value: string, shouldWrap: boolean) {
    return shouldWrap ? `(${value})` : value;
  }

  /**
   * Wraps the given value with single quotation marks if shouldWrap is true.
   * @param value The value to wrap
   * @param shouldWrap Whether to wrap the value with single quotation marks
   * @returns The wrapped value if shouldWrap is true, the original value otherwise
   **/
  public static wrapWithQuotationMarks(value: string, shouldWrap: boolean) {
    return shouldWrap ? `"${value}"` : value;
  }

  /**
   * Checks if a filter is a comparison filter for section metadata.
   * A comparison filter for section metadata is a filter with kind 'comparisonSectionFilter'
   * and analyticItem that does not include 'sectionProperties'.
   * @param filter The filter to check
   * @returns True if the filter is a comparison filter for section metadata, false otherwise
   */
  public static isSectionMetadataComparisonFilter(filter: Filter): boolean {
    return (
      filter &&
      this.isComparisonSectionFilter(filter) &&
      !filter.analyticItem.includes('sectionProperties')
    );
  }

  /**
   * Checks if a filter is a comparison filter for section metadata.
   * @param filter The filter to check
   * @returns True if the filter is a comparison filter for section metadata, false otherwise
   */
  public static isComparisonSectionFilter(filter: Filter): boolean {
    return filter?.kind === 'comparisonSectionFilter';
  }

  /**
   * Converts a comparison filter into an array of SelectedSection.
   * Given a comparison filter, it will iterate over the 'and', 'or', and 'not' operators
   * and convert any filter that is a comparison filter for section metadata into a SelectedSection.
   * @param comparison The comparison filter to convert
   * @param activeComparisonBucketIndex The index of the filter in the comparison to convert
   * @returns An array of SelectedSection representing the filters for section metadata
   */
  public static comparisonBucketToSelectedSections(
    comparison: Comparison,
    activeComparisonBucketIndex: number
  ): SelectedSection[] {
    const comparisonSectionFilter = [];
    if (!comparison || !comparison.filters[activeComparisonBucketIndex]) {
      return comparisonSectionFilter;
    }
    const compFilter = comparison.filters[activeComparisonBucketIndex];
    for (const operator of ['and', 'or', 'not']) {
      compFilter[operator].forEach((filter: Filter) => {
        if (this.isComparisonSectionFilter(filter)) {
          const negated = operator === 'not';
          comparisonSectionFilter.push(this.comparisonFilterToSelectedSection(filter, '', negated));
        }
      });
    }
    return comparisonSectionFilter;
  }

  /**
   * Converts a comparison filter into a SelectedSection.
   * Given a comparison filter, it will create a SelectedSection with the filter's analyticItem as the field,
   * the filter's value as the bucket, and the type as 'property'.
   * The prefix parameter can be used to add a prefix to the field name.
   * @param filter The comparison filter to convert
   * @param prefix The prefix to add to the field name
   * @returns A SelectedSection representing the filter
   */
  public static comparisonFilterToSelectedSection(
    filter: Filter,
    prefix: string = '',
    negated: boolean = false
  ): SelectedSection {
    if (!filter) {
      return undefined;
    }
    return {
      field: `${prefix}${filter.analyticItem}`,
      buckets: [filter.value],
      type: 'property',
      negated,
    };
  }

  /**
   * Given an AnalyticItemFilterSets object, this function will return a FilterSet containing all the comparison filters.
   * The function will iterate over the 'and', 'or', and 'not' operators of the sectionProperties filters and any filter that is a comparison filter will be added to the FilterSet.
   * If no sectionProperties filters are found, the function will return undefined.
   * @param filters The AnalyticItemFilterSets object to parse
   * @returns A FilterSet containing all the comparison filters
   */
  public static getComparisonSectionFilters(filters: AnalyticItemFilterSets): FilterSet {
    if (!filters.sectionProperties) {
      return undefined;
    }
    const comparisonSectionFilters: FilterSet = {
      and: [],
      or: [],
      not: [],
    };
    for (const operator of ['and', 'or', 'not']) {
      filters.sectionProperties[operator].forEach((filter: Filter) => {
        if (this.isComparisonSectionFilter(filter)) {
          comparisonSectionFilters[operator].push(filter);
        }
      });
    }
    return comparisonSectionFilters;
  }

  /**
   * Returns a new WidgetQueryOptions object with the comparisonSectionFilters applied to the selectedSection property.
   * If the widgetQueryOptions parameter is null or undefined, a new WidgetQueryOptions object will be created with the comparisonSectionFilters applied to the selectedSection property.
   * If the widgetQueryOptions parameter is not null or undefined, the comparisonSectionFilters will be merged into the selectedSection property of the existing widgetQueryOptions object.
   * @param widgetQueryOptions The WidgetQueryOptions object to apply the comparisonSectionFilters to
   * @param comparisonSectionFilters The SelectedSection[] array to apply to the selectedSection property of the widgetQueryOptions object
   * @returns A new WidgetQueryOptions object with the comparisonSectionFilters applied to the selectedSection property
   */
  public static getWidgetQueryOptionsWithSelectedSectionsApplied(
    widgetQueryOptions: WidgetQueryOptions,
    comparisonSectionFilters: SelectedSection[]
  ) {
    const updatedPartialWidgetQueryOptions: Partial<WidgetQueryOptions> = {
      ...(widgetQueryOptions ?? {}),
      selectedSection: [
        ...(widgetQueryOptions?.selectedSection ?? []),
        ...comparisonSectionFilters,
      ],
    };
    return updatedPartialWidgetQueryOptions as WidgetQueryOptions;
  }

  /**
   * Applies a FilterSet to a target AnalyticItemFilterSets object.
   * If the target AnalyticItemFilterSets object does not contain the specified targetFilterSetKey, it will be added with default values.
   * The function will then merge the sourceFilterSet's 'and', 'or', and 'not' arrays into the target AnalyticItemFilterSets object.
   * @param sourceFilterSet The FilterSet to apply to the target AnalyticItemFilterSets object
   * @param targetFilterSets The AnalyticItemFilterSets object to apply the sourceFilterSet to
   * @param targetFilterSetKey The key of the target AnalyticItemFilterSets object to apply the sourceFilterSet to
   */
  public static applyFilterSet(
    sourceFilterSet: FilterSet,
    targetFilterSets: AnalyticItemFilterSets,
    targetFilterSetKey: keyof AnalyticItemFilterSets
  ): void {
    if (!sourceFilterSet) {
      return;
    }
    targetFilterSets[targetFilterSetKey] ??= {
      and: [],
      or: [],
      not: [],
    };

    targetFilterSets[targetFilterSetKey] = {
      ...targetFilterSets[targetFilterSetKey],
      and: [...targetFilterSets[targetFilterSetKey].and, ...(sourceFilterSet.and ?? [])],
      or: [...targetFilterSets[targetFilterSetKey].or, ...(sourceFilterSet.or ?? [])],
      not: [...targetFilterSets[targetFilterSetKey].not, ...(sourceFilterSet.not ?? [])],
    };
  }

  /**
   * Checks if the widget query options have in-sections filter
   * @param widgetQueryOptions The widget query options to check
   * @returns True if the widget query options have in-sections filter, false otherwise
   */
  public static hasInSectionsFilter(widgetQueryOptions: WidgetQueryOptions): boolean {
    return !!widgetQueryOptions?.selectedSection?.length;
  }

  /**
   * Checks if the widget query options have parent in-sections filter
   * @param widgetQueryOptions The widget query options to check
   * @returns True if the widget query options have parent in-sections filter, false otherwise
   */
  public static hasParentInSectionsFilter(widgetQueryOptions: WidgetQueryOptions): boolean {
    return !!widgetQueryOptions?.parentSelectedSections?.length;
  }
}
